import { Camera, type Intersection, PerspectiveCamera, OrthographicCamera } from 'three'
import * as THREE from 'three'
import { useMapStore } from '@/stores/mapStore.ts'
import { nextTick } from 'vue'
import { useShipActionStore } from '@/stores/shipActionStore.ts'
import { useGameStore } from '@/stores/gameStore.ts'
import { useMapShipStore } from '@/stores/mapShipStore.ts'
import { useMapCoordinateStore } from '@/stores/mapCoordinateStore.ts'
import { useMapPlanetOwnerStore } from '@/stores/mapPlanetOwnerStore.ts'
import { zoomFactor } from '@/enums/zoomFactor.ts'
import { MapShipActionPaths } from '@/models/Map/MapShipActionPaths.ts'
import { useTableStore } from '@/stores/tableStore.ts'
import { MapCrossHairUpdate } from '@/models/Map/MapCrosshair.ts'
import { useMapSettingsStore } from '@/stores/mapSettingsStore.ts'
import { useRouteEditorStore } from '@/stores/routeEditorStore.ts'
import { useRouteStore } from '@/stores/routeStore.ts'
import { pointToLineDistance } from '@/utils/geometry.ts'
import * as Sentry from '@sentry/vue'
import { logger } from '@/models/logger'
import { throttle } from 'lodash'

// Fast check if object has any keys (avoids Object.keys() array allocation)
function hasKeys(obj: Record<string | number, unknown>): boolean {
  for (const _ in obj) return true
  return false
}

export class MapMouseController {
  isDragging: boolean
  startMousePosition = { x: 0, y: 0 }
  dragMousePosition = { x: 0, y: 0 }
  container: HTMLElement
  camera: Camera
  scene: THREE.Scene
  raycaster: THREE.Raycaster
  mouse: THREE.Vector2
  mapStore: any
  gameStore: any
  tableStore: any
  shipActionPaths: any
  hoverXpos: number
  hoverYpos: number
  shipActionStore: any
  routeEditorStore: any
  routeStore: any
  mapPlanetOwnerStore: any
  mapCoordinateStore: any
  mapShipStore: any
  renderer: THREE.WebGLRenderer
  isTouchDragging: boolean
  lastTouchPosition: { x: number; y: number }
  // Zoom constants
  minZoomFactor: number = 0.1 // Max zoom out (corresponds to zoomFactor.Max)
  maxZoomFactor: number = 10 // Max zoom in (corresponds to zoomFactor.Planet)
  zoomSpeed: number = 0.001 // How fast zooming occurs
  // Middle mouse button state
  isMiddleMouseDown: boolean = false

  constructor(
    container: HTMLElement,
    camera: THREE.Camera,
    scene: THREE.Scene,
    renderer: THREE.WebGLRenderer
  ) {
    this.container = container
    this.camera = camera
    this.scene = scene
    this.renderer = renderer
    this.isDragging = false
    this.isTouchDragging = false
    this.lastTouchPosition = { x: 0, y: 0 }

    // Apply touch-action: none to prevent default browser touch behaviors
    this.container.style.touchAction = 'none'

    this.raycaster = new THREE.Raycaster()
    this.mouse = new THREE.Vector2()

    this.mapStore = useMapStore()
    this.gameStore = useGameStore()
    this.shipActionStore = useShipActionStore()
    this.routeEditorStore = useRouteEditorStore()
    this.routeStore = useRouteStore()
    this.mapPlanetOwnerStore = useMapPlanetOwnerStore()
    this.mapCoordinateStore = useMapCoordinateStore()
    this.mapShipStore = useMapShipStore()
    this.tableStore = useTableStore()

    // Ship Action paths, for when hovering over ships (to show their paths)
    this.shipActionPaths = new MapShipActionPaths(this.scene)
    this.hoverXpos = 0
    this.hoverYpos = 0

    // Define the area selector box
    const boxVertices = new Float32Array([
      0,
      0,
      0.09, // Top-left corner
      0,
      0,
      0.09, // Top-right corner
      0,
      0,
      0.09, // Bottom-right corner
      0,
      0,
      0.09, // Bottom-left corner
      0,
      0,
      0.09 // Back to Top-left to close the loop
    ])
    const geometry = new THREE.BufferGeometry()
    geometry.setAttribute('position', new THREE.BufferAttribute(boxVertices, 3))
    const material = new THREE.LineBasicMaterial({ color: 0x0000ff })
    this.mapStore.areaBox = new THREE.Line(geometry, material)
    this.mapStore.areaBox.visible = false
    this.mapStore.areaBox.frustumCulled = false
    this.scene.add(this.mapStore.areaBox)

    // Bind the methods to the instance
    this.onMouseDown = this.onMouseDown.bind(this)
    this.onMouseLeave = this.onMouseLeave.bind(this)
    this.onMouseMove = this.onMouseMove.bind(this)
    this.onMouseUp = this.onMouseUp.bind(this)
    this.onClick = this.onClick.bind(this)
    this.onWheel = this.onWheel.bind(this)
    this.onTouchStart = this.onTouchStart.bind(this)
    this.onTouchMove = this.onTouchMove.bind(this)
    this.onTouchEnd = this.onTouchEnd.bind(this)
    this.onAuxClick = this.onAuxClick.bind(this)

    this.container.addEventListener('mousedown', this.onMouseDown)
    this.container.addEventListener('mouseleave', this.onMouseLeave)
    this.container.addEventListener('mousemove', this.onMouseMove)
    this.container.addEventListener('mouseup', this.onMouseUp)
    this.container.addEventListener('click', this.onClick)
    this.container.addEventListener('wheel', this.onWheel)
    this.container.addEventListener('touchstart', this.onTouchStart, { passive: false })
    this.container.addEventListener('touchmove', this.onTouchMove, { passive: false })
    this.container.addEventListener('touchend', this.onTouchEnd)
    this.container.addEventListener('auxclick', this.onAuxClick) // For middle mouse button clicks
  }

  destroy() {
    // Remove event listeners when destroying the instance
    this.container.removeEventListener('mousedown', this.onMouseDown)
    this.container.removeEventListener('mouseleave', this.onMouseLeave)
    this.container.removeEventListener('mousemove', this.onMouseMove)
    this.container.removeEventListener('mouseup', this.onMouseUp)
    this.container.removeEventListener('click', this.onClick)
    this.container.removeEventListener('wheel', this.onWheel)
    this.container.removeEventListener('auxclick', this.onAuxClick)
  }

  // Control rendering quality based on zooming state
  setRenderQuality(highQuality: boolean) {
    // If the renderer supports quality settings, apply them here
    if (highQuality) {
      // Restore high quality rendering after zoom
      // Clamp pixel ratio between 1.0 and 1.5 (instead of using full devicePixelRatio)
      const clampedRatio = Math.min(Math.max(window.devicePixelRatio, 1.0), 1.5)
      if (this.renderer.getPixelRatio() < clampedRatio) {
        this.renderer.setPixelRatio(clampedRatio)
      }
    } else {
      // Reduce quality during zoom for better performance
      // Always set to 1.0 during zooming
      if (this.renderer.getPixelRatio() > 1.0) {
        this.renderer.setPixelRatio(1.0)
      }
    }
  }

  onMouseDown(event: MouseEvent) {
    this.mapStore.playerNameTooltipVisible = false

    // Track middle mouse button state
    if (event.button === 1) {
      // Middle mouse button
      this.isMiddleMouseDown = true
      event.preventDefault()
    }

    // Handle right-click for route editor (delete waypoint only if near one)
    if (event.button === 2 && this.routeEditorStore.initialized) {
      this.updateMousePosition(event)
      const deleted = this.routeEditorRightClick(event)
      if (deleted) {
        return // Don't start map dragging when deleting waypoint
      }
      // If no waypoint was deleted, continue to allow map panning
    }

    this.startDrag(event)
    this.mapClick(event) // moved to mouse down because of the area selection
  }

  onMouseLeave(event: MouseEvent) {
    this.stopDrag(event)
  }

  // Cached raycast results for sharing between functions (avoids duplicate raycasting)
  private cachedIntersects: THREE.Intersection[] = []
  private cachedIntersectsFrame: number = 0
  private frameCounter: number = 0

  // Throttled mouse move to reduce raycasting frequency (32ms = ~30fps)
  throttledMouseMoveHandler = throttle((event: MouseEvent) => {
    this.handleMouseMoveOperations(event)
  }, 32)

  onMouseMove(event: MouseEvent) {
    if (this.isDragging) {
      this.dragMap(event)
      // Skip expensive operations while dragging for better performance
      return
    }
    this.updateMousePosition(event)
    this.areaSelectMove(event)
    this.throttledMouseMoveHandler(event)
  }

  // Combined mouse move operations with shared raycasting
  private handleMouseMoveOperations(event: MouseEvent) {
    this.frameCounter++
    // Do raycasting once and cache the results
    this.raycaster.setFromCamera(this.mouse, this.camera)
    this.cachedIntersects = this.raycaster.intersectObjects(this.scene.children)
    this.cachedIntersectsFrame = this.frameCounter

    // Pass cached intersects to both functions
    this.mapMoveWithIntersects(event, this.cachedIntersects)
    this.hoverEventWithIntersects(event, this.cachedIntersects)
  }

  onMouseUp(event: MouseEvent) {
    // Reset middle mouse button state
    if (event.button === 1) {
      this.isMiddleMouseDown = false
    }

    // Stop dragging waypoint in route editor
    if (this.routeEditorStore.initialized && this.routeEditorStore.draggingWaypointIndex !== null) {
      this.routeEditorStore.stopDraggingWaypoint()
      this.setCursor('crosshair')
    }

    this.stopDrag(event)
    this.areaSelectEnd(event)
    //this.mapStore.loadVisibleSectors();
  }

  onClick(event: MouseEvent) {
    this.updateMousePosition(event)
  }
  /*************************** Hover *************************/

  hoverEvent(_event: MouseEvent) {
    this.raycaster.setFromCamera(this.mouse, this.camera)
    const intersects = this.raycaster.intersectObjects(this.scene.children)
    this.hoverEventWithIntersects(_event, intersects)
  }

  hoverEventWithIntersects(_event: MouseEvent, intersects: THREE.Intersection[]) {

    let found = false
    let planetIntersectionData = null

    // First pass - collect data and check for ships specifically
    for (const intersect of intersects) {
      const data = this.getCoordinateData(intersect)
      if (!data) continue // Skip if data is null or undefined

      // Check for ships first
      if (data.hasPlayerShips) {
        // Prevent creating a new path if the mouse is still on the same coordinate
        if (this.hoverXpos !== data.xPos || this.hoverYpos !== data.yPos) {
          this.shipActionPaths.create(data.xPos, data.yPos)
          this.hoverXpos = data.xPos
          this.hoverYpos = data.yPos
        }

        // Show tooltip for player ship
        this.mapStore.playerNameTooltipVisible = true
        this.mapStore.playerNameTooltipOwnerId = this.gameStore.player?.id || 0
        this.mapStore.playerNameTooltipShipCount = data.ships[this.gameStore.player?.id] || 0
        this.mapStore.playerNameTooltipName = ''
        this.mapStore.playerNameTooltipCompatibility = ''

        found = true
        break // Exit early if we found a ship
      }

      // Store planet data for later if no ships are found
      if (data.isPlanet) {
        planetIntersectionData = data
      }

      if (data.isWormhole) {
        found = true
      }
    }

    // If we didn't find a ship with direct hover, check if there are ships near the planet
    if (!found && planetIntersectionData) {
      const planetX = planetIntersectionData.xPos
      const planetY = planetIntersectionData.yPos

      // Process planet data for tooltips first (prioritize planet over nearby ships)
      if (planetIntersectionData.planetOwnerId !== 0) {
        // Show tooltip with name
        this.mapStore.playerNameTooltipVisible = true
        this.mapStore.playerNameTooltipOwnerId = planetIntersectionData.planetOwnerId
        this.mapStore.playerNameTooltipShipCount =
          planetIntersectionData.ships[planetIntersectionData.planetOwnerId] || 0
        this.mapStore.playerNameTooltipName = ''
        this.mapStore.playerNameTooltipCompatibility = ''
        found = true
      }

      // Check for unowned planets without ships
      if (!found && planetIntersectionData && planetIntersectionData.planetOwnerId === 0 && !hasKeys(planetIntersectionData.ships)) {
        this.mapStore.playerNameTooltipVisible = true
        this.mapStore.playerNameTooltipOwnerId = 0
        this.mapStore.playerNameTooltipShipCount = 0
        this.mapStore.playerNameTooltipName = ''
        this.mapStore.playerNameTooltipCompatibility = planetIntersectionData.compatibility
        found = true
      }

      // Only search for ships in orbit if the planet doesn't have an owner (so no planet tooltip to show)
      if (!found) {
        // Expanded search for ships around the planet (orbit range)
        for (let dx = -3; dx <= 3; dx++) {
          for (let dy = -3; dy <= 3; dy++) {
            const orbitX = planetX + dx
            const orbitY = planetY + dy

            if (this.mapCoordinateStore.hasByCoordinates(orbitX, orbitY)) {
              const orbitData = this.mapCoordinateStore.getDataForRayCasting(orbitX, orbitY)
              if (!orbitData) continue // Skip if data is null or undefined

              if (orbitData.hasPlayerShips) {
                // Found a ship in orbit
                this.shipActionPaths.create(orbitX, orbitY)
                this.hoverXpos = orbitX
                this.hoverYpos = orbitY

                // Show tooltip for player ship
                this.mapStore.playerNameTooltipVisible = true
                this.mapStore.playerNameTooltipOwnerId = this.gameStore.player?.id || 0
                this.mapStore.playerNameTooltipShipCount =
                  orbitData.ships[this.gameStore.player?.id] || 0
                this.mapStore.playerNameTooltipName = ''
                this.mapStore.playerNameTooltipCompatibility = ''

                found = true
                break
              }
            }
          }
          if (found) break
        }
      }

      //Auto select planet if it is the player
      if (planetIntersectionData.planetOwnerId == this.gameStore.player?.id) {
        this.tableStore.planets.singleSelectedId = planetIntersectionData.id
      }
    }

    // Process other intersections if we still haven't found anything
    if (!found) {
      for (const intersect of intersects) {
        const data = this.getCoordinateData(intersect)
        if (!data) continue // Skip if data is null or undefined

        if (hasKeys(data.ships)) {
          this.mapStore.playerNameTooltipVisible = true
          this.mapStore.playerNameTooltipOwnerId = Object.keys(data.ships).pop()
          this.mapStore.playerNameTooltipShipCount =
            data.ships[this.mapStore.playerNameTooltipOwnerId]
          this.mapStore.playerNameTooltipName = ''
          this.mapStore.playerNameTooltipCompatibility = ''
          found = true
          break
        }
      }
    }

    // Check for stars with no players or ships
    if (!found) {
      for (const intersect of intersects) {
        const data = this.getCoordinateData(intersect)
        if (!data) continue // Skip if data is null or undefined

        if (data.isStar && !hasKeys(data.ships)) {
          this.mapStore.playerNameTooltipVisible = true
          this.mapStore.playerNameTooltipOwnerId = 0
          this.mapStore.playerNameTooltipShipCount = 0
          this.mapStore.playerNameTooltipName = data.typeName
          this.mapStore.playerNameTooltipCompatibility = ''
          found = true
          break
        }
      }
    }

    // Clean up if nothing was found
    if (this.shipActionPaths !== null && !found) {
      this.shipActionPaths.deleteLines()
      this.hoverXpos = 0
      this.hoverYpos = 0
    }

    if (!found) {
      this.mapStore.playerNameTooltipVisible = false
    }

    // Check for route hover (show route name tooltip)
    this.checkRouteHover(intersects)
  }

  /**
   * Check if hovering over a saved route and show tooltip
   */
  checkRouteHover(intersects: Array<THREE.Intersection>) {
    const mapSettingsStore = useMapSettingsStore()

    // Don't check if routes are hidden or if route editor is active
    if (!mapSettingsStore.showRoutes || this.routeEditorStore.initialized) {
      this.mapStore.routeTooltipVisible = false
      return
    }

    for (const intersect of intersects) {
      if (intersect.object.name === 'backPlane') {
        const x = Math.ceil((intersect as any).point.x)
        const y = Math.ceil((intersect as any).point.y)

        // Check each route to see if we're hovering over it
        for (const route of this.routeStore.routes.values()) {
          const waypoints = route.waypoints
          if (waypoints.length < 2) continue

          // Check each line segment (including loop back)
          for (let i = 0; i < waypoints.length; i++) {
            const p1 = waypoints[i]
            const p2 = waypoints[(i + 1) % waypoints.length]
            const distance = pointToLineDistance(x, y, p1.x, p1.y, p2.x, p2.y)
            if (distance <= 5) {
              this.mapStore.routeTooltipVisible = true
              this.mapStore.routeTooltipName = route.name
              this.setCursor('pointer')
              return
            }
          }
        }
        break
      }
    }

    this.mapStore.routeTooltipVisible = false
  }

  /*************************** Move *************************/

  mapMove(event: MouseEvent) {
    this.raycaster.setFromCamera(this.mouse, this.camera)
    const intersects = this.raycaster.intersectObjects(this.scene.children)
    this.mapMoveWithIntersects(event, intersects)
  }

  mapMoveWithIntersects(event: MouseEvent, intersects: THREE.Intersection[]) {
    // Handle route editor mouse move for pending waypoint preview
    if (this.routeEditorStore.initialized) {
      this.routeEditorMove(event, intersects)
    } else if (this.shipActionStore.pendingAction !== '') {
      this.mapPendingAction(event, intersects)
    } else {
      this.mapMoveToolTip(intersects)
    }

    this.getLocation(event, intersects)
  }

  /**
   * Handle mouse move for route editor - drag waypoint if dragging, or update cursor
   */
  routeEditorMove(_event: MouseEvent, intersects: Array<THREE.Intersection>) {
    // If dragging a waypoint, update its position
    if (this.routeEditorStore.draggingWaypointIndex !== null) {
      for (const intersect of intersects) {
        if (intersect.object.name === 'backPlane') {
          const x = Math.ceil((intersect as any).point.x)
          const y = Math.ceil((intersect as any).point.y)
          this.routeEditorStore.updateWaypoint(this.routeEditorStore.draggingWaypointIndex, x, y)
          this.setCursor('grabbing')
          return
        }
      }
    }

    // Check if hovering near a waypoint or line
    for (const intersect of intersects) {
      if (intersect.object.name === 'backPlane') {
        const x = Math.ceil((intersect as any).point.x)
        const y = Math.ceil((intersect as any).point.y)

        // Check waypoints first (higher priority)
        const waypointIndex = this.routeEditorStore.findWaypointNear(x, y, 5)
        if (waypointIndex >= 0) {
          this.setCursor('grab')
          return
        }

        // Check line segments (to insert waypoint)
        const segmentIndex = this.routeEditorStore.findLineSegmentNear(x, y, 5)
        if (segmentIndex >= 0) {
          this.setCursor('cell') // Plus cursor to indicate insertion
          return
        }
        break
      }
    }

    this.setCursor('crosshair')
  }

  getLocation(event: MouseEvent, intersects: Array<Intersection>) {
    for (const intersect of intersects) {
      if ((intersect as any).object.name === 'backPlane') {
        this.mapStore.mouseXPos = Math.ceil((intersect as any).point.x)
        this.mapStore.mouseYPos = Math.ceil((intersect as any).point.y)
        break
      }
    }
  }

  /**
   * Handle pending action when moving mouse
   *
   * @param event
   * @param intersects
   */
  mapPendingAction(event: MouseEvent, intersects: Array<Intersection>) {
    for (const intersect of intersects) {
      //Detect if this is the backPlane
      if (intersect.object.name === 'backPlane') {
        const data = this.getCoordinateData(intersect)
        if (this.shipActionStore.pendingAction === 'move') {
          if (data.xPos == 0) {
            if (event.shiftKey) {
              // Free Movement Enabled
              this.setCursor('move')
              this.mapStore.shipActionPath.removePendingAction()
              this.mapStore.shipActionPath.setPendingAction(
                (intersect as any).point.x,
                (intersect as any).point.y
              )
              this.renderMap()
              break
            } else {
              // Clear Selection for grid movement
              this.setCursor('default')
              this.mapStore.shipActionPath.removePendingAction()
              this.renderMap()
              break
            }
          } else {
            // Grid Movement Enabled
            this.setCursor('move')
            this.mapStore.shipActionPath.setPendingAction(data.xPos, data.yPos)
            this.renderMap()
            break
          }
        } else if (this.shipActionStore.pendingAction === 'follow') {
          if (data.xPos == 0) {
            // Clear Selection for grid movement
            this.setCursor('default')
            this.mapStore.shipActionPath.removePendingAction()
            this.renderMap()
            break
          } else if (data.canBeFollowed) {
            // Grid Movement Enabled
            this.setCursor('move')
            this.mapStore.shipActionPath.setPendingAction(data.xPos, data.yPos)
            this.renderMap()
            break
          }
        } else if (this.shipActionStore.pendingAction === 'followAttack') {
          if (data.xPos == 0) {
            // Clear Selection for grid movement
            this.setCursor('default')
            this.mapStore.shipActionPath.removePendingAction()
            this.renderMap()
            break
          } else if (data.canBeFollowed) {
            // Grid Movement Enabled
            this.setCursor('move')
            this.mapStore.shipActionPath.setPendingAction(data.xPos, data.yPos)
            this.renderMap()
            break
          }
        } else if (this.shipActionStore.pendingAction === 'explore') {
          break
        } else if (this.shipActionStore.pendingAction === 'repair') {
          break
        } else if (this.shipActionStore.pendingAction === 'attack') {
          if (data.xPos == 0) {
            this.mapStore.shipActionPath.removePendingAction()
            this.renderMap()
          } else {
            // Attack the selected position
            this.mapStore.shipActionPath.setPendingAction(data.xPos, data.yPos)
            this.renderMap()
            this.setCursor('crosshair')
            break
          }
        } else if (this.shipActionStore.pendingAction === 'settle') {
          if (data.xPos == 0) {
            this.mapStore.shipActionPath.removePendingAction()
            this.renderMap()
          } else if (data.canBeSettled) {
            // Settle the selected position
            this.mapStore.shipActionPath.setPendingAction(data.xPos, data.yPos)
            this.renderMap()
            this.setCursor('crosshair')
            break
          }
        } else {
          this.setCursor('default')
        }
      }
    }
  }

  /**
   * Display cursor on planes that can do tooltips
   * @param intersects
   */
  mapMoveToolTip(intersects: Array<Intersection>) {
    for (const intersect of intersects) {
      // Check for black hole
      if (intersect.object.name === 'blackHole') {
        this.setCursor('pointer')
        return
      }
      const data = this.getCoordinateData(intersect)
      if (data.hasTooltip) {
        this.setCursor('pointer')
        break
      } else {
        this.setCursor('default')
      }
    }
  }

  /**
   * Figure out what we are hovering on
   * @param intersect
   */
  getCoordinateData(intersect: Intersection) {
    if ((intersect as any).object.name === 'backPlane') {
      const x = Math.ceil((intersect as any).point.x)
      const y = Math.ceil((intersect as any).point.y)

      // Increase the detection radius for all zoom levels
      // to ensure we can detect ships and show their action paths
      let radius = 2 // Default minimum radius increased from 1

      if (this.mapStore.zoomFactor === zoomFactor.Planet) {
        radius = 2 // Increased from 1
      } else if (this.mapStore.zoomFactor === zoomFactor.Solar) {
        radius = 2 // Increased from 1
      } else if (this.mapStore.zoomFactor === zoomFactor.Local) {
        radius = 4 // Increased from 3
      } else if (this.mapStore.zoomFactor === zoomFactor.Sector) {
        radius = 6 // Increased from 4
      } else if (this.mapStore.zoomFactor === zoomFactor.Quadrant) {
        radius = 8 // Increased from 5
      } else if (this.mapStore.zoomFactor === zoomFactor.Max) {
        radius = 10 // Increased from 6
      } else {
        // For intermediate zoom levels, calculate radius based on nearest preset zoom levels
        if (
          this.mapStore.zoomFactor > zoomFactor.Solar &&
          this.mapStore.zoomFactor < zoomFactor.Planet
        ) {
          // Between Solar and Planet
          radius = 2
        } else if (
          this.mapStore.zoomFactor > zoomFactor.Local &&
          this.mapStore.zoomFactor < zoomFactor.Solar
        ) {
          // Between Local and Solar
          radius = 3
        } else if (
          this.mapStore.zoomFactor > zoomFactor.Sector &&
          this.mapStore.zoomFactor < zoomFactor.Local
        ) {
          // Between Sector and Local
          radius = 5
        } else if (
          this.mapStore.zoomFactor > zoomFactor.Quadrant &&
          this.mapStore.zoomFactor < zoomFactor.Sector
        ) {
          // Between Quadrant and Sector
          radius = 7
        } else if (
          this.mapStore.zoomFactor > zoomFactor.Max &&
          this.mapStore.zoomFactor < zoomFactor.Quadrant
        ) {
          // Between Max and Quadrant
          radius = 9
        }
      }

      const list = this.getNearbyCoordinates(x, y, radius)
      for (const coords of list) {
        if (this.mapCoordinateStore.hasByCoordinates(coords.x, coords.y)) {
          return this.mapCoordinateStore.getDataForRayCasting(coords.x, coords.y) // Exits the loop
        }
      }
      return {
        id: 0,
        isPlanet: false,
        isWormhole: false,
        planetOwnerId: 0,
        canBeSettled: false,
        hasTooltip: false,
        hasPlayerShips: false,
        ships: {},
        xPos: 0,
        yPos: 0
      }
    } else {
      return {
        id: 0,
        isPlanet: false,
        isWormhole: false,
        planetOwnerId: 0,
        canBeSettled: false,
        hasTooltip: false,
        hasPlayerShips: false,
        ships: {},
        xPos: 0,
        yPos: 0
      }
    }
  }

  // Cache offset patterns per radius to avoid recomputing
  private static offsetCache: Map<number, Array<{ dx: number; dy: number }>> = new Map()

  getNearbyCoordinates(x: number, y: number, radius: number) {
    // Get or create cached offsets for this radius
    let offsets = MapMouseController.offsetCache.get(radius)
    if (!offsets) {
      offsets = []
      for (let dx = -radius; dx <= radius; dx++) {
        for (let dy = -radius; dy <= radius; dy++) {
          offsets.push({ dx, dy })
        }
      }
      MapMouseController.offsetCache.set(radius, offsets)
    }

    // Apply offsets to create coordinates
    const nearbyCoords = []
    for (const offset of offsets) {
      nearbyCoords.push({ x: x + offset.dx, y: y + offset.dy })
    }
    return nearbyCoords
  }
  /*************************** click *************************/

  /**
   * Process click on map
   *
   * @param event
   */
  async mapClick(event: MouseEvent) {
    if (event.button === 0) {
      if (this.mapStore.toolTipVisible || this.mapStore.blackHoleTooltipVisible) {
        this.mapStore.toolTipVisible = false
        this.mapStore.blackHoleTooltipVisible = false
        await nextTick()
      }

      if (this.hasDragged(event)) {
        this.stopDrag(event)
      } else {
        // Handle route editor clicks first
        if (this.routeEditorStore.initialized) {
          this.routeEditorClick(event)
          return
        }

        if (this.shipActionStore.pendingAction !== '') {
          this.pendingActionClick(event)
        } else {
          // Try to open tooltip first (ships/planets take priority over route lines)
          if (this.openToolTip(event)) {
            return
          }

          // Check if clicking on a saved route to edit it
          if (this.checkRouteClick(event)) {
            return
          }

          // No tooltip and no route, so can start the area selection
          this.areaSelectStart(event)
        }
      }
    }
  }

  /**
   * Check if clicking on a saved route - if so, open it in edit mode
   * Returns true if a route was clicked, false otherwise
   */
  checkRouteClick(_event: MouseEvent): boolean {
    const mapSettingsStore = useMapSettingsStore()

    // Don't check if routes are hidden
    if (!mapSettingsStore.showRoutes) {
      return false
    }

    this.raycaster.setFromCamera(this.mouse, this.camera)
    const intersects = this.raycaster.intersectObjects(this.scene.children)

    for (const intersect of intersects) {
      if (intersect.object.name === 'backPlane') {
        const x = Math.ceil((intersect as any).point.x)
        const y = Math.ceil((intersect as any).point.y)

        // Check each route to see if we clicked on it
        for (const route of this.routeStore.routes.values()) {
          const waypoints = route.waypoints
          if (waypoints.length < 2) continue

          // Check each line segment (including loop back)
          for (let i = 0; i < waypoints.length; i++) {
            const p1 = waypoints[i]
            const p2 = waypoints[(i + 1) % waypoints.length]
            const distance = pointToLineDistance(x, y, p1.x, p1.y, p2.x, p2.y)
            if (distance <= 5) {
              // Open route in edit mode
              this.routeEditorStore.startEditRoute(route.id)
              return true
            }
          }
        }
        break
      }
    }

    return false
  }

  /**
   * Handle click for route editor - add waypoint, start dragging, or insert between existing
   */
  routeEditorClick(_event: MouseEvent) {
    this.raycaster.setFromCamera(this.mouse, this.camera)
    const intersects = this.raycaster.intersectObjects(this.scene.children)

    for (const intersect of intersects) {
      if (intersect.object.name === 'backPlane') {
        const x = Math.ceil((intersect as any).point.x)
        const y = Math.ceil((intersect as any).point.y)

        // Check if clicking near an existing waypoint (to start dragging)
        const waypointIndex = this.routeEditorStore.findWaypointNear(x, y, 5)
        if (waypointIndex >= 0) {
          // Start dragging this waypoint
          this.routeEditorStore.startDraggingWaypoint(waypointIndex)
          this.setCursor('grabbing')
        } else {
          // Check if clicking on a line segment between waypoints
          const segmentIndex = this.routeEditorStore.findLineSegmentNear(x, y, 5)
          if (segmentIndex >= 0) {
            // Insert new waypoint between existing ones
            this.routeEditorStore.insertWaypointAfter(segmentIndex, x, y)
          } else {
            // Add new waypoint at the end
            this.routeEditorStore.addWaypoint(x, y)
          }
        }
        this.renderMap()
        break
      }
    }
  }

  /**
   * Handle right-click for route editor - delete waypoint if clicking near one
   * Returns true if a waypoint was deleted, false otherwise
   */
  routeEditorRightClick(event: MouseEvent): boolean {
    this.raycaster.setFromCamera(this.mouse, this.camera)
    const intersects = this.raycaster.intersectObjects(this.scene.children)

    for (const intersect of intersects) {
      if (intersect.object.name === 'backPlane') {
        const x = Math.ceil((intersect as any).point.x)
        const y = Math.ceil((intersect as any).point.y)

        // Check if right-clicking near an existing waypoint (to delete)
        const waypointIndex = this.routeEditorStore.findWaypointNear(x, y, 5)
        if (waypointIndex >= 0) {
          event.preventDefault()
          this.routeEditorStore.removeWaypoint(waypointIndex)
          this.renderMap()
          return true
        }
        break
      }
    }
    return false
  }

  areaSelectStart(event: MouseEvent) {
    if (event.button !== 0) return
    if (this.shipActionStore.initialized) return

    this.mapStore.areaStartTime = Date.now()
    this.mapStore.areaStart = { x: this.mapStore.mouseXPos, y: this.mapStore.mouseYPos } // Can use mapStore if applicable
    this.mapStore.areaSelecting = true
    this.mapStore.areaActionSelecting = false

    // Set initial size of the box
    this.updateAreaBox(
      this.mapStore.areaStart.x,
      this.mapStore.areaStart.y,
      this.mapStore.areaStart.x,
      this.mapStore.areaStart.y
    )

    logger.debug('Area Select Start')
  }

  areaSelectMove(_event: MouseEvent) {
    if (!this.mapStore.areaSelecting) return
    if (this.shipActionStore.initialized) return

    this.mapStore.areaBox.visible = true

    this.mapStore.areaEnd = { x: this.mapStore.mouseXPos, y: this.mapStore.mouseYPos }

    // Dynamically resize the box
    this.updateAreaBox(
      this.mapStore.areaStart.x,
      this.mapStore.areaStart.y,
      this.mapStore.areaEnd.x,
      this.mapStore.areaEnd.y
    )

    logger.debug('Area Select Move')
  }

  areaSelectEnd(event: MouseEvent) {
    if (event.button !== 0) return
    if (!this.mapStore.areaSelecting) return
    if (this.shipActionStore.initialized) return

    const elapsedTime = Date.now() - this.mapStore.areaStartTime

    if (elapsedTime > 200) {
      logger.debug('Area Select End')
      const rect = this.container.getBoundingClientRect()
      this.mapStore.areaMouseEnd = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top
      }
      this.mapStore.areaSelecting = false
      this.mapStore.areaActionSelecting = true

      // Determine the top-left and bottom-right coordinates
      this.mapStore.areaBoxTL = {
        x: Math.min(this.mapStore.areaStart.x, this.mapStore.areaEnd.x),
        y: Math.max(this.mapStore.areaStart.y, this.mapStore.areaEnd.y)
      }

      this.mapStore.areaBoxRB = {
        x: Math.max(this.mapStore.areaStart.x, this.mapStore.areaEnd.x),
        y: Math.min(this.mapStore.areaStart.y, this.mapStore.areaEnd.y)
      }

      logger.debug('Area Select Time: ' + elapsedTime)
      logger.debug('Area Select Start: ' + JSON.stringify(this.mapStore.areaStart))
      logger.debug('Area Select End: ' + JSON.stringify(this.mapStore.areaEnd))
    } else {
      logger.debug('Area Select End (Too Fast)')
      this.mapStore.disableAreaSelection()
    }
  }

  updateAreaBox(x1: number, y1: number, x2: number, y2: number) {
    const boxVertices = new Float32Array([
      x1,
      y1,
      1.09, // Top-left corner
      x2,
      y1,
      1.09, // Top-right corner
      x2,
      y2,
      1.09, // Bottom-right corner
      x1,
      y2,
      1.09, // Bottom-left corner
      x1,
      y1,
      1.09 // Back to Top-left to close the loop
    ])

    const geometry = this.mapStore.areaBox.geometry as THREE.BufferGeometry
    geometry.setAttribute('position', new THREE.BufferAttribute(boxVertices, 3))
    geometry.attributes.position.needsUpdate = true // Ensure the update happens
  }
  pendingActionClick(_event: MouseEvent) {
    this.raycaster.setFromCamera(this.mouse, this.camera)
    const intersects = this.raycaster.intersectObjects(this.scene.children)
    for (const intersect of intersects) {
      if (intersect.object.name === 'backPlane') {
        const data = this.getCoordinateData(intersect)

        if (this.shipActionStore.pendingAction === 'move') {
          // Process Movement
          let x = 0
          let y = 0
          if (data.xPos == 0) {
            // Free Movement
            x = Math.ceil((intersect as any).point.x)
            y = Math.ceil((intersect as any).point.y)
          } else {
            // Grid Movement
            x = data.xPos
            y = data.yPos
          }
          this.mapStore.shipActionPath.queuePendingAction(x, y, 0, 0)
          this.renderMap()
          break
        } else if (this.shipActionStore.pendingAction === 'follow') {
          if (data.xPos != 0) {
            // Add Sentry breadcrumb to track modal opening
            Sentry.addBreadcrumb({
              category: 'ship-action',
              message: 'Opening follow modal',
              level: 'info',
              data: {
                action: 'follow',
                xPos: data.xPos,
                yPos: data.yPos,
                hasCoordinate: this.mapCoordinateStore.hasByCoordinates(data.xPos, data.yPos),
                fleetId: this.shipActionStore.fleetId,
                shipsCount: this.shipActionStore.ships.length
              }
            })
            this.gameStore.modalType = 'mapFollow'
            this.gameStore.modalData = {
              xPos: data.xPos,
              yPos: data.yPos
            }
            break
          }
        } else if (this.shipActionStore.pendingAction === 'followAttack') {
          if (data.xPos != 0) {
            // Add Sentry breadcrumb to track modal opening
            Sentry.addBreadcrumb({
              category: 'ship-action',
              message: 'Opening followAttack modal',
              level: 'info',
              data: {
                action: 'followAttack',
                xPos: data.xPos,
                yPos: data.yPos,
                hasCoordinate: this.mapCoordinateStore.hasByCoordinates(data.xPos, data.yPos),
                fleetId: this.shipActionStore.fleetId,
                shipsCount: this.shipActionStore.ships.length
              }
            })
            this.gameStore.modalType = 'mapFollowAttack'
            this.gameStore.modalData = {
              xPos: data.xPos,
              yPos: data.yPos
            }
            break
          }
        } else if (this.shipActionStore.pendingAction === 'attack') {
          if (data.xPos != 0) {
            // Add Sentry breadcrumb to track modal opening
            Sentry.addBreadcrumb({
              category: 'ship-action',
              message: 'Opening attack modal',
              level: 'info',
              data: {
                action: 'attack',
                xPos: data.xPos,
                yPos: data.yPos,
                hasCoordinate: this.mapCoordinateStore.hasByCoordinates(data.xPos, data.yPos),
                fleetId: this.shipActionStore.fleetId,
                shipsCount: this.shipActionStore.ships.length
              }
            })
            this.gameStore.modalType = 'mapAttack'
            this.gameStore.modalData = {
              xPos: data.xPos,
              yPos: data.yPos
            }
            break
          }
        } else if (this.shipActionStore.pendingAction === 'settle' && data.canBeSettled) {
          if (data.xPos != 0) {
            this.shipActionStore.queuePendingAction(data.xPos, data.yPos, 0, 0)
            this.renderMap()
            break
          }
        }
      }
    }
  }

  /**
   * Open tooltip on map
   *
   * @param event
   */
  openToolTip(_event: MouseEvent): boolean {
    this.raycaster.setFromCamera(this.mouse, this.camera)
    const intersects = this.raycaster.intersectObjects(this.scene.children)
    for (const intersect of intersects) {
      const data = this.getCoordinateData(intersect)
      if (data.hasTooltip) {
        this.mapStore.blackHoleTooltipVisible = false
        this.mapStore.tooltipX = data.xPos
        this.mapStore.tooltipY = data.yPos
        this.mapStore.toolTipVisible = true
        return true
      }
      if (intersect.object.name === 'blackHole') {
        this.mapStore.toolTipVisible = false
        this.mapStore.blackHoleTooltipVisible = true
        return true
      }
    }
    return false
  }

  /*************************** Dragging *************************/

  /**
   * Start Dragging
   *
   * @param event
   */
  startDrag(event: MouseEvent) {
    // Right click should start dragging
    if (event.button === 2) {
      this.startMousePosition = { x: event.clientX, y: event.clientY }
      this.dragMousePosition = { x: event.clientX, y: event.clientY }
      this.isDragging = true
    }
  }

  /**
   * Stop Dragging
   *
   * @param event
   */
  stopDrag(_event: MouseEvent) {
    // Right click should start dragging
    if (this.isDragging) {
      this.isDragging = false
      this.setCursor('default')
    }
  }

  /**
   * After click, did we drag?
   *
   *
   * @param event
   * @param threshold
   */
  hasDragged(event: MouseEvent, threshold: number = 5): boolean {
    // Right click should start dragging
    if (event.button === 2) {
      const deltaX = Math.abs(this.startMousePosition.x - event.clientX)
      const deltaY = Math.abs(this.startMousePosition.y - event.clientY)

      return deltaX > threshold || deltaY > threshold
    }
    return false
  }

  /**
   * Process drag
   *
   * @param event
   */
  async dragMap(event: MouseEvent) {
    // Right click should start dragging
    this.setCursor('grabbing')
    const deltaX = event.clientX - this.dragMousePosition.x
    const deltaY = event.clientY - this.dragMousePosition.y
    this.camera.position.x -= deltaX * (0.5 / this.mapStore.zoomFactor)
    this.camera.position.y += deltaY * (0.5 / this.mapStore.zoomFactor)
    this.mapStore.cameraXPos = this.camera.position.x
    this.mapStore.cameraYPos = this.camera.position.y
    this.dragMousePosition = { x: event.clientX, y: event.clientY }
    this.renderMap()

    // Hide area selection and menu when dragging/scrolling the map
    if (this.mapStore.areaSelecting || this.mapStore.areaActionSelecting) {
      this.mapStore.disableAreaSelection()
    }

    if (this.mapStore.crossHairVisible) {
      this.mapStore.crossHairVisible = false
      new MapCrossHairUpdate(this.scene, this.mapStore.zoomFactor)
    }
  }

  /*************************** Tools *************************/

  /**
   * Update the mouse position on map
   *
   * @param event
   */
  updateMousePosition(event: MouseEvent) {
    const rect = this.container.getBoundingClientRect()
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1
  }

  /**
   * Render map
   */
  renderMap() {
    this.renderer.render(this.scene, this.camera)
  }

  /**
   * Set cursor on map
   * @param type
   */
  setCursor(type: string) {
    this.container.style.cursor = type
  }

  onWheel(event: WheelEvent) {
    // Check if mouse wheel zoom is enabled
    const mapSettingsStore = useMapSettingsStore()

    if (mapSettingsStore.enableMouseWheelZoom) {
      // ZOOMING BEHAVIOR
      // Reduce quality during zoom for better performance
      this.setRenderQuality(false)

      // Calculate the new zoom factor
      let newZoomFactor = this.mapStore.zoomFactor - event.deltaY * this.zoomSpeed

      // Clamp the zoom factor between min and max values
      newZoomFactor = Math.max(this.minZoomFactor, Math.min(this.maxZoomFactor, newZoomFactor))

      // Apply the new zoom factor (watcher in MapWebGL.vue handles applyZoomFactorAll)
      this.mapStore.zoomFactor = newZoomFactor

      // Update ship action paths ETA
      if (this.shipActionPaths) {
        this.shipActionPaths.updateZoom(newZoomFactor)
      }

      // Update the camera zoom
      if (this.camera instanceof PerspectiveCamera || this.camera instanceof OrthographicCamera) {
        this.camera.zoom = newZoomFactor
        this.camera.updateProjectionMatrix()
      }

      // Render the changes
      this.renderMap()

      // Restore quality after a brief delay
      setTimeout(() => {
        this.setRenderQuality(true)
        this.renderMap()
      }, 100)
    } else {
      // PANNING BEHAVIOR - Use wheel for panning when zoom is disabled
      event.preventDefault()

      // Determine pan amount (adjust these values as needed for desired sensitivity)
      const panSpeed = 10 / this.mapStore.zoomFactor

      // Handle both vertical and horizontal scroll
      // Most mouse wheels scroll vertically, but some can scroll horizontally
      // Shift+wheel is also often used for horizontal scrolling

      // Vertical scroll (or primary scroll direction)
      if (event.deltaY !== 0) {
        this.camera.position.y += event.deltaY * 0.01 * panSpeed
        this.mapStore.cameraYPos = this.camera.position.y
      }

      // Horizontal scroll (often shift+wheel or trackpad horizontal gesture)
      if (event.deltaX !== 0) {
        this.camera.position.x -= event.deltaX * 0.01 * panSpeed
        this.mapStore.cameraXPos = this.camera.position.x
      }

      // Render the changes
      this.renderMap()

      // Update crosshair if needed
      if (this.mapStore.crossHairVisible) {
        this.mapStore.crossHairVisible = false
        new MapCrossHairUpdate(this.scene, this.mapStore.zoomFactor)
      }
    }
  }

  onTouchStart(event: TouchEvent) {
    if (event.touches.length !== 1) {
      return
    }

    this.isTouchDragging = true
    this.lastTouchPosition = { x: event.touches[0].clientX, y: event.touches[0].clientY }
  }

  onTouchMove(event: TouchEvent) {
    if (event.touches.length !== 1) {
      return
    }

    if (this.isTouchDragging) {
      event.preventDefault() // Prevent default scrolling behavior

      // Calculate the movement delta
      const touch = event.touches[0]
      const deltaX = (touch.clientX - this.lastTouchPosition.x) * (0.5 / this.mapStore.zoomFactor)
      const deltaY = (touch.clientY - this.lastTouchPosition.y) * (0.5 / this.mapStore.zoomFactor)

      // Update the camera position
      this.camera.position.x -= deltaX
      this.camera.position.y += deltaY
      this.mapStore.cameraXPos = this.camera.position.x
      this.mapStore.cameraYPos = this.camera.position.y

      // Store the new position for the next move event
      this.lastTouchPosition = { x: touch.clientX, y: touch.clientY }

      // Update the rendering to reflect the new camera position
      this.renderMap()
    }
  }

  onTouchEnd(event: TouchEvent) {
    if (event.touches.length === 0) {
      this.isTouchDragging = false
    }
  }

  // Handle middle mouse button clicks
  onAuxClick(event: MouseEvent) {
    if (event.button === 1) {
      // Middle mouse button
      event.preventDefault()

      // Toggle zoom between min and max zoom levels
      let newZoomValue
      if (this.mapStore.zoomFactor > (this.minZoomFactor + this.maxZoomFactor) / 2) {
        newZoomValue = this.minZoomFactor
      } else {
        newZoomValue = this.maxZoomFactor
      }

      // Apply the new zoom factor (watcher in MapWebGL.vue handles applyZoomFactorAll)
      this.mapStore.zoomFactor = newZoomValue

      // Update ship action paths ETA
      if (this.shipActionPaths) {
        this.shipActionPaths.updateZoom(newZoomValue)
      }

      // Update the camera zoom
      if (this.camera instanceof PerspectiveCamera || this.camera instanceof OrthographicCamera) {
        this.camera.zoom = newZoomValue
        this.camera.updateProjectionMatrix()
      }

      // Render the changes
      this.renderMap()
    }
  }
}
